import { Button, Modal, Input, Select, Space, InputNumber } from 'antd';
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from 'src/api';
import { toast } from 'react-hot-toast';
import styled from 'styled-components';

const StyledSpace = styled(Space)`
  display: flex;
  width: 100%;

  .ant-space-item:first-child {
    flex-grow: 1;
  }
`;

function AddCalendarModal({
  isOpen,
  close,
  selectedCalendar,
  propertyId,
  rooms,
  isFetching,
  propertyType,
  isMobile
}) {
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [room, setRoom] = useState('');
  const [count, setCount] = useState(1);
  const queryClient = useQueryClient();
  
  const clean = () => {
    setName('');
    setUrl('');
    setRoom('');
    setCount(1);
    close();
  };
  
  useEffect(() => {
    setName(selectedCalendar?.name);
    setUrl(selectedCalendar?.url);
    if (propertyType === 'hotel') {
      setRoom(selectedCalendar?.roomTypeId);
      setCount(selectedCalendar?.roomCount || 1);
    }
  }, [selectedCalendar, propertyType]);

  const { mutate, isPending } = useMutation({
    mutationFn: async () => {
      let res = {};
      if (!name) return toast.error('Name is required');
      if (!url) return toast.error('URL is required');
      if (!room && propertyType === 'hotel')
        return toast.error('Room is required');
      if (selectedCalendar) {
        res = await api.put(`/calendar-sync`, {
          calendarId: selectedCalendar._id,
          name,
          url,
          roomCount: count
        });
      } else {
        res = await api.post(`/calendar-sync`, {
          propertyId,
          name,
          url,
          ...(propertyType === 'hotel' && { roomTypeId: room }),
          ...(propertyType === 'hotel' && { roomCount: count })
        });
      }
      if (res?.data?.status === 'success') {
        toast.success(selectedCalendar ? 'Calendar Updated' : 'Calendar Added');
        queryClient.invalidateQueries(['calendars', propertyId]);
        clean();
      }
    },
    onError: () => {
      toast.error('Something went wrong');
    }
  });

  const modalStyle = {
    top: isMobile ? 20 : 100,
    maxWidth: isMobile ? '95vw' : '520px',
    margin: isMobile ? '0 auto' : undefined
  };

  const headingStyle = {
    fontSize: isMobile ? '14px' : '16px',
    margin: isMobile ? '10px 0 5px' : '15px 0 8px',
    fontWeight: 500
  };

  const controlSize = isMobile ? 'middle' : 'large';

  return (
    <Modal
      open={isOpen}
      title={selectedCalendar ? 'Update Calendar' : 'Add Calendar'}
      onCancel={clean}
      footer={[
        <Button key="back" onClick={clean} disabled={isPending} size={controlSize}>
          Cancel
        </Button>,
        <Button
          key="link"
          type="primary"
          disabled={isPending}
          loading={isPending}
          onClick={mutate}
          form="sync-calendar"
          size={controlSize}
        >
          Save
        </Button>
      ]}
      style={modalStyle}
      width={isMobile ? '95%' : 520}
      maskClosable={!isPending}
      destroyOnClose
      centered={!isMobile}
    >
      {!!selectedCalendar ? (
        <div style={{ padding: isMobile ? '5px 0' : '10px 0' }}>
          <h4 style={headingStyle}>Name</h4>
          <Input
            size={controlSize}
            placeholder="Name"
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <h4 style={headingStyle}>URL</h4>
          <Input
            size={controlSize}
            placeholder="Url"
            value={url}
            onChange={e => setUrl(e.target.value)}
          />
          {propertyType === 'hotel' && (
            <>
              <StyledSpace>
                <Space size={0} direction="vertical" style={{ width: '100%' }}>
                  <h4 style={headingStyle}>Room</h4>
                  <Input
                    disabled
                    size={controlSize}
                    placeholder={
                      rooms.find(item => item.roomTypeId === room)?.roomName ||
                      ''
                    }
                  />
                </Space>
                <Space size={0} direction="vertical" style={{ width: '100%' }}>
                  <h4 style={headingStyle}>Room Count</h4>
                  <InputNumber
                    size={controlSize}
                    disabled={!room}
                    min={1}
                    value={count}
                    onChange={setCount}
                    max={rooms.find(item => item.roomTypeId === room)?.count}
                    style={{ width: '100%' }}
                  />
                </Space>
              </StyledSpace>
            </>
          )}
        </div>
      ) : (
        <div style={{ padding: isMobile ? '5px 0' : '10px 0' }}>
          <h4 style={headingStyle}>Name</h4>
          <Input
            size={controlSize}
            placeholder="Name"
            value={name}
            onChange={e => setName(e.target.value)}
          />

          <h4 style={headingStyle}>URL</h4>
          <Input
            size={controlSize}
            placeholder="Url"
            value={url}
            onChange={e => setUrl(e.target.value)}
          />
          {propertyType === 'hotel' && (
            <StyledSpace>
              <Space size={0} direction="vertical" style={{ width: '100%' }}>
                <h4 style={headingStyle}>Room Type</h4>
                <Select
                  size={controlSize}
                  style={{
                    width: '100%'
                  }}
                  loading={isFetching}
                  placeholder="Select a room"
                  onChange={val => setRoom(val)}
                  options={rooms.map(item => ({
                    value: item.roomTypeId,
                    label: item.roomName
                  }))}
                  dropdownMatchSelectWidth={!isMobile}
                  listHeight={isMobile ? 200 : 256}
                />
              </Space>
              <Space size={0} direction="vertical" style={{ width: '100%' }}>
                <h4 style={headingStyle}>Room Count</h4>
                <InputNumber
                  size={controlSize}
                  disabled={!room}
                  min={1}
                  value={count}
                  onChange={setCount}
                  max={rooms.find(item => item.roomTypeId === room)?.count}
                  style={{ width: '100%' }}
                />
              </Space>
            </StyledSpace>
          )}
        </div>
      )}
    </Modal>
  );
}

export default AddCalendarModal;